import { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, ChevronDown, X } from 'lucide-react';
import {
  startOfMonth, startOfWeek, endOfWeek, addDays, addMonths,
  isSameDay, isSameMonth, isBefore, isAfter, isValid, format, parse,
} from 'date-fns';

// ─── DatePicker — shared themed single-date picker ───────────────────────────
// Replaces native <input type="date"> for new/converted work, the same way DarkSelect
// replaces native <select> (CLAUDE_RULES: never use a native form control for new work).
//
// Visual design + behaviour ported from watermelon.sh's `date-picker-3` registry item
// (icon-left trigger + chevron, popover calendar, rounded-full day cells, filled selected
// day, muted "today", close-on-select). Tailwind / Base UI / Radix stripped out — this is
// plain inline styles on the ProQure CSS-variable tokens (see DESIGN_SYSTEM.md), built and
// consumed the same shape as DarkSelect.tsx (portal popover, outside-click close,
// flip-up-when-no-room).
//
// Dates are handled in LOCAL time only: an incoming `value` may be 'YYYY-MM-DD' OR a full
// ISO string from the API (`.slice(0,10)` guard — CLAUDE_RULES bug #12), and the emitted
// value is always a bare 'YYYY-MM-DD' produced with date-fns `format` (never toISOString,
// which would roll a US-Eastern user back a day).
//
// The popover carries a `data-datepicker-popover` marker (purely additive, no visual/behavioral
// change for any existing caller) so a PARENT wrapper that manages its own "editing" state around
// a DatePicker instance (e.g. Orders.tsx's inline table-cell editors) can tell "click landed inside
// the portal popover" apart from "click landed truly outside everything" without this component
// needing to expose its internal open state. This component's own outside-click handling (above)
// is unaffected either way.

const ISO_FMT = 'yyyy-MM-dd';
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function parseValue(v?: string | null): Date | null {
  if (!v) return null;
  const d = parse(v.slice(0, 10), ISO_FMT, new Date());
  return isValid(d) ? d : null;
}
function toValue(d: Date): string {
  return format(d, ISO_FMT);
}

export default function DatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  min,
  max,
  disabled = false,
  clearable = true,
  id,
  style,
  fitContent = false,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  clearable?: boolean;
  id?: string;
  // Optional trigger-style override, merged over the default `.form-input` look.
  // Use it where the original native input carried a LOCAL custom style (e.g. a
  // `rgba(255,255,255,0.06)` glassy field with tighter padding) instead of the shared
  // `.form-input` class — pass that local convention's background / padding / fontSize /
  // border / borderRadius / color here so the picker matches its sibling fields. The
  // focus/open border highlight + ring stay applied on top regardless.
  style?: React.CSSProperties;
  // Default (false): trigger stretches to 100% of its container, same as a native form field --
  // right for a .form-group column. true: trigger shrinks to its own content (date text + icon +
  // chevron) instead of stretching to whatever width the surrounding layout happens to hand it,
  // with only a tight, fixed gap between the text and the chevron -- for compact contexts like an
  // inline table-cell editor, where "stretch to fill the column" produced a large empty gap
  // between the date text and the chevron (the column's rendered width, driven by HTML's own
  // widest-cell-in-the-column sizing, can end up much wider than the date text needs). A new,
  // explicit, opt-in prop rather than overriding `width`/`display` via `style` -- those are
  // documented below as "fixed layout — not overridable" for a reason, so this is a deliberate
  // escape hatch, not a way around that contract.
  fitContent?: boolean;
}) {
  const selected = useMemo(() => parseValue(value), [value]);
  const minDate = useMemo(() => parseValue(min), [min]);
  const maxDate = useMemo(() => parseValue(max), [max]);

  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(() => startOfMonth(selected ?? new Date()));
  const [focusedDay, setFocusedDay] = useState<Date>(() => selected ?? new Date());
  const [hoverDay, setHoverDay] = useState<Date | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; width: number }>({ left: 0, width: 0 });

  // Re-sync the visible month + keyboard cursor each time the popover opens.
  useEffect(() => {
    if (open) {
      const base = selected ?? new Date();
      setViewMonth(startOfMonth(base));
      setFocusedDay(base);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Outside-click close — same pattern as DarkSelect.
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        (!popoverRef.current || !popoverRef.current.contains(e.target as Node))
      ) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function openPopover() {
    if (disabled) return;
    if (!open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const estH = 348; // header + 6 rows + footer, approx
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < estH && rect.top > estH) {
        setPos({ bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width });
      } else {
        setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    }
    setOpen((o) => !o);
  }

  function isDisabledDay(d: Date): boolean {
    if (minDate && isBefore(d, minDate)) return true;
    if (maxDate && isAfter(d, maxDate)) return true;
    return false;
  }

  function commit(d: Date) {
    if (isDisabledDay(d)) return;
    onChange(toValue(d));
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') { e.preventDefault(); openPopover(); }
      return;
    }
    if (e.key === 'Escape' || e.key === 'Tab') { setOpen(false); return; }
    let next: Date | null = null;
    if (e.key === 'ArrowLeft') next = addDays(focusedDay, -1);
    else if (e.key === 'ArrowRight') next = addDays(focusedDay, 1);
    else if (e.key === 'ArrowUp') next = addDays(focusedDay, -7);
    else if (e.key === 'ArrowDown') next = addDays(focusedDay, 7);
    else if (e.key === 'PageUp') next = addMonths(focusedDay, -1);
    else if (e.key === 'PageDown') next = addMonths(focusedDay, 1);
    else if (e.key === 'Home') next = startOfWeek(focusedDay, { weekStartsOn: 0 });
    else if (e.key === 'End') next = endOfWeek(focusedDay, { weekStartsOn: 0 });
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); commit(focusedDay); return; }
    if (next) {
      e.preventDefault();
      setFocusedDay(next);
      if (!isSameMonth(next, viewMonth)) setViewMonth(startOfMonth(next));
    }
  }

  // 6-week grid starting Sunday, same as the watermelon calendar.
  const gridStart = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 0 });
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const today = new Date();

  const active = focused || open;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: fitContent ? 'fit-content' : '100%' }}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={openPopover}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          // fixed layout — not overridable (except width, deliberately, via the fitContent prop)
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: fitContent ? 'auto' : '100%',
          fontFamily: 'inherit',
          textAlign: 'left',
          boxSizing: 'border-box',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.55 : 1,
          transition: 'border-color 150ms ease, box-shadow 150ms ease',
          // themeable defaults (match `.form-input`) — overridable via `style`
          padding: '9px 12px',
          background: 'var(--bg-elevated)',
          fontSize: '0.875rem',
          borderRadius: 6,
          color: selected ? 'var(--text-primary)' : 'var(--text-muted)',
          border: '1px solid var(--border-default)',
          ...style,
          // focus / open highlight always wins — recolors whatever border `style` set,
          // keeping its width + line style.
          ...(active
            ? { borderColor: 'var(--accent-primary)', boxShadow: '0 0 0 3px rgba(59,130,246,0.15)' }
            : { boxShadow: 'none' }),
        }}
      >
        <CalendarIcon size={15} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
        <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selected ? format(selected, 'MMM d, yyyy') : placeholder}
        </span>
        {clearable && selected && !disabled && (
          <span
            role="button"
            aria-label="Clear date"
            onClick={(e) => { e.stopPropagation(); onChange(''); setOpen(false); }}
            style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--text-muted)', borderRadius: 4 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
          >
            <X size={13} />
          </span>
        )}
        <ChevronDown size={15} style={{ flexShrink: 0, opacity: 0.5, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }} />
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={popoverRef}
          data-datepicker-popover=""
          style={{
            position: 'fixed',
            ...(pos.top !== undefined ? { top: pos.top } : { bottom: pos.bottom }),
            left: pos.left,
            width: 268,
            zIndex: 10001,
            background: 'var(--bg-elevated)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 10,
            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
            overflow: 'hidden',
            userSelect: 'none',
          }}
        >
          {/* Month nav */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px 6px' }}>
            <NavBtn label="Previous month" onClick={() => setViewMonth((m) => addMonths(m, -1))}>
              <ChevronLeft size={16} />
            </NavBtn>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              {format(viewMonth, 'MMMM yyyy')}
            </span>
            <NavBtn label="Next month" onClick={() => setViewMonth((m) => addMonths(m, 1))}>
              <ChevronRight size={16} />
            </NavBtn>
          </div>

          {/* Weekday header */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '0 12px' }}>
            {WEEKDAYS.map((w, i) => (
              <div key={i} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', padding: '4px 0' }}>
                {w}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, padding: '2px 12px 10px' }}>
            {days.map((d, i) => {
              const outside = !isSameMonth(d, viewMonth);
              const isSelected = !!selected && isSameDay(d, selected);
              const isToday = isSameDay(d, today);
              const isDisabled = isDisabledDay(d);
              const isCursor = open && isSameDay(d, focusedDay);
              const isHover = !!hoverDay && isSameDay(d, hoverDay);

              let bg = 'transparent';
              let color = outside ? 'var(--text-muted)' : 'var(--text-primary)';
              let fontWeight = 400;
              if (isSelected) { bg = 'var(--accent-primary)'; color = '#fff'; fontWeight = 700; }
              else if (isToday) { bg = 'rgba(255,255,255,0.08)'; fontWeight = 700; }
              if (isHover && !isSelected && !isDisabled) bg = 'var(--bg-hover)';
              if (isDisabled) { color = 'var(--text-muted)'; }

              return (
                <button
                  key={i}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => commit(d)}
                  onMouseEnter={() => setHoverDay(d)}
                  onMouseLeave={() => setHoverDay(null)}
                  style={{
                    height: 32,
                    borderRadius: 999,
                    border: isCursor && !isSelected ? '1px solid rgba(59,130,246,0.6)' : '1px solid transparent',
                    background: bg,
                    color,
                    fontWeight,
                    fontSize: 12,
                    fontFamily: 'inherit',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.35 : 1,
                    padding: 0,
                    transition: 'background 120ms ease',
                  }}
                >
                  {format(d, 'd')}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 12px', borderTop: '1px solid var(--border-subtle)',
          }}>
            <button
              type="button"
              onClick={() => commit(today)}
              disabled={isDisabledDay(today)}
              style={{
                background: 'none', border: 'none', padding: 0,
                color: isDisabledDay(today) ? 'var(--text-muted)' : 'var(--accent-primary)',
                fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                cursor: isDisabledDay(today) ? 'not-allowed' : 'pointer',
              }}
            >
              Today
            </button>
            {clearable && selected && (
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); }}
                style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text-muted)', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer' }}
              >
                Clear
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function NavBtn({ children, label, onClick }: { children: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 26, height: 26, borderRadius: 6, border: 'none',
        background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
    >
      {children}
    </button>
  );
}
